import {inject, injectable} from "inversify";
import {Country} from "@/modules/base/entity";
import CupCompetitionFactory from "@/modules/competitions/services/factories/CupCompetitionFactory";
import KODivisionMatchGenerator from "@/modules/competitions/services/divisions/KODivisionMatchGenerator";
import TeamFactory from "@/modules/team/services/factories/TeamFactory";
import {Season} from "@/modules/season/entity";
import {Team} from "@/modules/team/entity";
import LeagueCompetitionFactory from "@/modules/competitions/services/factories/LeagueCompetitionFactory";
import {SectionLoader} from "@/modules/base/services/SectionLoader";
import {nonblocking} from "@/utils";
import FastPlayerFactory from "@/modules/team/services/factories/FastPlayerFactory";

@injectable()
export default class CountryCompetitionFactory {
  constructor(
    @inject(CupCompetitionFactory) private cupCompetitionFactory: CupCompetitionFactory,
    @inject(LeagueCompetitionFactory) private leagueCompetitionFactory: LeagueCompetitionFactory,
    @inject(KODivisionMatchGenerator) private koDivisionMatchGenerator: KODivisionMatchGenerator,
    @inject(TeamFactory) private teamFactory: TeamFactory,
    @inject(FastPlayerFactory) private fastPlayerFactory: FastPlayerFactory,
  ) {
  }

  async generate(country: Country, divisions = 1, teamsPerDivision = 18, options: { noLeague?: boolean, noCup?: boolean } = {}, loader: SectionLoader): Promise<void> {
    const totalTeamCount = divisions * teamsPerDivision;
    const season = await Season.current();

    const teams = await loader.processSection(`teams`, async (advance: (step?: number) => void) => {
      const teams = await times(divisions)
        .mapAsync(async (i: number) => {
          if (i === divisions - 1) {
            advance();

            return [
              await Team.current(),
              ...await times(teamsPerDivision - 1)
                .mapAsync(async (j: number) => {
                  const team = await this.teamFactory
                    .generate(
                      country,
                      Math.max(20, (1 - (i * teamsPerDivision + j) / totalTeamCount) * 100)
                    );

                  advance();

                  return team;
                })
            ];
          }

          return await times(teamsPerDivision)
            .mapAsync(async (j: number) => {
              const team = await this.teamFactory
                .generate(country, Math.max(20, (1 - (i * teamsPerDivision + j) / totalTeamCount) * 100));

              advance();

              return team;
            });
        });

      await nonblocking(() => this.fastPlayerFactory.flush());

      return teams;
    });

    await nonblocking(async () => {
      await loader.processSection(`leagues`, async (advance: (step: number) => void) => {
        if (!options.noLeague) {
          await this.generateLeague(season, country, teams);
        }
      });
    });

    await nonblocking(async () => {
      await loader.processSection(`cups`, async (advance: (step: number) => void) => {
        if (!options.noCup) {
          await this.generateCup(season, country, collect(teams).flatten(1).reverse().toArray());
        }
      });
    });
  }

  async generateLeague(season: Season, country: Country, teams: Team[][]) {
    const league = await this.leagueCompetitionFactory.generate(season, 'National league', teams, {
      relegateAndPromote: 3,
      basePrice: 10000
    });
    league.country = country;
    await league.save();
  }

  async generateCup(season: Season, country: Country, teams: Team[]) {
    const cupRounds = Math.floor(Math.log2(teams.length));

    const cup = await this.cupCompetitionFactory.generate(season, 'National cup', cupRounds, true);
    cup.country = country;
    cup.winner = teams[0];
    await cup.save();
    await cup.reload();

    const firstCupDivision = cup.divisions[0];
    firstCupDivision.teams = collect(teams).take(2 ** cupRounds).toArray();
    await firstCupDivision.save();
    console.debug('assigned teams to division: ' + firstCupDivision.name);

    await this.koDivisionMatchGenerator.generate(season, firstCupDivision);
    console.debug('generate matches for division: ' + firstCupDivision.name);
  }
}
